// The library states (consistency pass, R2; Kristi, Nov 2: rules L1–L5, answers 1a, 2a, 3a, 4a).
// One ordered list of eight states, most urgent first, with one set of words each. Shelves (the
// alert line, the Library filter row, card pills) and My books › Library copies (its sections, tab
// dot and needs line) all classify loans here, so they always agree. No database calls, so the
// rules are tested on their own (tests/logic/library-states.test.ts).
//
//   #  state        needs you  in a summary            on a card, row or the book page
//   1  loan_ended   yes        1 loan ended            Loan ended Oct 28
//   2  ready        yes        2 ready                 Ready · borrow by Nov 3
//   3  due_soon     yes        1 due soon              Due Nov 4 · Due today
//   4  check_libby  yes        1 to check in Libby     Check Libby · expected Nov 6
//   5  not_started  yes        1 not started           Not started · due Nov 19
//   6  on_loan      quiet      2 on loan               Due Nov 19
//   7  on_hold      quiet      6 on hold               On hold · expected Jan 13, 2027
//   8  to_place     quiet      4 holds to place        —

import { fmtNum } from '../ui/ListFoot';
import { addDays, dayText, daysBetween } from '../book/model';

/** Due soon: due today or within this many days (the Renew window, Kristi Oct 29). */
export const DUE_SOON_DAYS = 3;

/**
 * Check Libby's window (R2 question 4, Kristi Nov 2: answer a): your library's own "check Libby N
 * days before the expected date" setting (libraries.check_days_before), everywhere, 7 when it's
 * missing. Every reader of the setting goes through checkDaysOf, so a different answer is one line.
 */
export const CHECK_DAYS_DEFAULT = 7;
export function checkDaysOf(v: unknown): number {
  if (v === null || v === undefined || v === '') return CHECK_DAYS_DEFAULT;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : CHECK_DAYS_DEFAULT;
}

export const LIB_STATES = ['loan_ended', 'ready', 'due_soon', 'check_libby', 'not_started', 'on_loan', 'on_hold', 'to_place'] as const;
export type LibState = (typeof LIB_STATES)[number];
/** A state a hold or loan can be in (Holds to place is a book with none). */
export type LoanState = Exclude<LibState, 'to_place'>;

/** The first five need you; the last three are quiet. */
export const NEEDS_YOU = ['loan_ended', 'ready', 'due_soon', 'check_libby', 'not_started'] as const;
export type NeedsKey = (typeof NEEDS_YOU)[number];
export const needsYou = (s: LibState | null | undefined): s is NeedsKey => Boolean(s && (NEEDS_YOU as readonly string[]).includes(s));

/** A hold or loan as both screens read it. */
export interface LoanLike {
  step: string;
  dueOn: string | null;
  expectedOn?: string | null;
  /** The library's check_days_before, as stored (checkDaysOf gives the window). */
  checkDays?: number | null;
  /** Checked, not ready yet (0051, Kristi Nov 2): a hold in its Check Libby window stays quiet through this day. */
  quietUntil?: string | null;
}

/** In its Check Libby window, by its expected date and your library's window. */
const inCheckWindow = (l: LoanLike, today: string) => Boolean(l.step === 'on_hold' && l.expectedOn && addDays(l.expectedOn, -checkDaysOf(l.checkDays)) <= today);

/**
 * A hold you've checked and quieted (filters review, Kristi Nov 2: "Not ready yet ▾"): it would be
 * Check Libby, but you said not yet, through `quietUntil`. It counts as on hold everywhere (no dot,
 * no line), and Library copies shows it grey at the foot of Check status.
 */
export const quietedHold = (l: LoanLike, today: string) => inCheckWindow(l, today) && Boolean(l.quietUntil && l.quietUntil >= today);

/**
 * One hold or loan's state. Borrowed: past its due date → loan ended; due today or within 3 days →
 * due soon; else not started when the book isn't on Currently Reading, on loan when it is. Ready →
 * ready. On hold: expected within your library's window, or past it → check Libby (unless you've
 * quieted it: Not ready yet); else on hold.
 * Returned and cancelled loans have none.
 */
export function loanState(l: LoanLike, reading: boolean, today: string): LoanState | null {
  if (l.step === 'borrowed') {
    if (l.dueOn && l.dueOn < today) return 'loan_ended';
    if (l.dueOn && l.dueOn <= addDays(today, DUE_SOON_DAYS)) return 'due_soon';
    return reading ? 'on_loan' : 'not_started';
  }
  if (l.step === 'ready') return 'ready';
  if (l.step === 'on_hold') return inCheckWindow(l, today) && !quietedHold(l, today) ? 'check_libby' : 'on_hold';
  return null;
}

export const stateRank = (s: LibState) => LIB_STATES.indexOf(s);

/** A book's state: its most urgent hold or loan's (a book with a Kindle and a Libby audio loan is one book). */
export function bookState(loans: LoanLike[], reading: boolean, today: string): LoanState | null {
  let best: LoanState | null = null;
  for (const l of loans) {
    const s = loanState(l, reading, today);
    if (s && (best === null || stateRank(s) < stateRank(best))) best = s;
  }
  return best;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** A state's words in a summary or heading: "1 loan ended", "2 ready", "1 to check in Libby". */
export const COUNT_WORDS: Record<LibState, (n: number) => string> = {
  loan_ended: (n) => plural(n, 'loan ended', 'loans ended'),
  ready: (n) => `${n} ready`,
  due_soon: (n) => `${n} due soon`,
  check_libby: (n) => `${n} to check in Libby`,
  not_started: (n) => `${n} not started`,
  on_loan: (n) => `${n} on loan`,
  on_hold: (n) => `${n} on hold`,
  to_place: (n) => plural(n, 'hold to place', 'holds to place'),
};

/** A state's name, as the Filters panel's Library chips and section headings say it. */
export const STATE_NAMES: Record<LibState, string> = {
  loan_ended: 'Loan ended',
  ready: 'Ready',
  due_soon: 'Due soon',
  check_libby: 'Check Libby',
  not_started: 'Not started',
  on_loan: 'On loan',
  on_hold: 'On hold',
  to_place: 'Holds to place',
};

export interface SummaryPart {
  key: NeedsKey;
  count: number;
  words: string;
}

/**
 * The needs-you parts that aren't zero, in order, each "count + words" (L2): counts only, no
 * dates. Takes each book's state once (bookState), so a book is counted under its most urgent part.
 */
export function needsParts(states: (LibState | null | undefined)[]): SummaryPart[] {
  const counts = new Map<NeedsKey, number>();
  for (const s of states) if (needsYou(s)) counts.set(s, (counts.get(s) ?? 0) + 1);
  return NEEDS_YOU.filter((k) => counts.has(k)).map((k) => ({ key: k, count: counts.get(k)!, words: COUNT_WORDS[k](counts.get(k)!) }));
}

/**
 * The one summary sentence (L2): "1 loan ended · 2 ready · 1 due soon · 1 to check in Libby · 1 not
 * started". Shelves' alert line, the line under Library copies' description and the tab dot's
 * tooltip ("Needs you: …") all say it. Empty when nothing needs you: no line, no dot.
 */
export const needsSentence = (parts: SummaryPart[]) => parts.map((p) => p.words).join(' · ');

/** The dates a state's words need, as the loan has them. */
export interface LoanDates {
  dueOn: string | null;
  borrowBy?: string | null;
  expectedOn?: string | null;
}

/**
 * A hold or loan's words on a card pill, a Library copies row and the book page's library line
 * (L5), with the short date (R8): "Loan ended Oct 28", "Ready · borrow by Nov 3", "Due Nov 4",
 * "Due today", "Check Libby · expected Nov 6", "Not started · due Nov 19", "On hold · expected
 * Jan 13, 2027". Always "expected" for a hold's date, never "around" or "about".
 */
export function stateWords(s: LoanState, l: LoanDates, today: string): string {
  const day = (d: string) => dayText(d, today);
  const due = l.dueOn ? (l.dueOn === today ? 'Due today' : `Due ${day(l.dueOn)}`) : null;
  switch (s) {
    case 'loan_ended':
      return l.dueOn ? `Loan ended ${day(l.dueOn)}` : 'Loan ended';
    case 'ready':
      return l.borrowBy ? `Ready · borrow by ${day(l.borrowBy)}` : 'Ready';
    case 'due_soon':
      return due ?? 'Due soon';
    case 'check_libby':
      return l.expectedOn ? `Check Libby · expected ${day(l.expectedOn)}` : 'Check Libby';
    case 'not_started':
      return due ? `Not started · ${due[0]!.toLowerCase()}${due.slice(1)}` : 'Not started';
    case 'on_loan':
      return due ?? 'Borrowed';
    case 'on_hold':
      return l.expectedOn ? `On hold · expected ${day(l.expectedOn)}` : 'On hold';
  }
}

/**
 * How far off a state's date is, for a Library copies row and the book page's library line (Kristi,
 * Nov 2: "3 days left" is useful; card pills stay short): "3 days left", "1 day left", "4 days ago",
 * "in 4 days", "2 days past expected". Nothing when the words already say it ("Due today") or a hold
 * is more than 30 days off.
 */
export function whenWords(s: LoanState, l: LoanDates, today: string): string {
  const days = (n: number) => plural(n, 'day', 'days');
  const left = (d: string | null | undefined) => {
    if (!d) return '';
    const n = daysBetween(today, d);
    return n > 0 ? `${days(n)} left` : '';
  };
  switch (s) {
    case 'loan_ended': {
      if (!l.dueOn) return '';
      const n = daysBetween(l.dueOn, today);
      return n > 0 ? `${days(n)} ago` : '';
    }
    case 'ready':
      return left(l.borrowBy);
    case 'due_soon':
    case 'not_started':
    case 'on_loan':
      return left(l.dueOn);
    case 'check_libby':
    case 'on_hold': {
      if (!l.expectedOn) return '';
      const n = daysBetween(today, l.expectedOn);
      if (n < 0) return `${days(-n)} past expected`;
      if (n === 0) return 'expected today';
      return n <= 30 ? `in ${days(n)}` : '';
    }
  }
}

/** "#3 in line · 2 copies" (both places say it this way, Kristi Nov 1). */
export function lineWords(placeInLine: number | null | undefined, copies: number | null | undefined): string {
  return [placeInLine ? `#${placeInLine} in line` : '', copies ? plural(copies, 'copy', 'copies') : ''].filter(Boolean).join(' · ');
}

/** Where a book's library copies stand now, in a history summary: "Now: borrowed, due Nov 19", "Now: ready to borrow", "Now: on hold"; empty when nothing is going. */
export function nowLine(loans: { step: string; dueOn: string | null }[], today: string): string {
  const b = loans.filter((l) => l.step === 'borrowed').sort((x, y) => (x.dueOn ?? '').localeCompare(y.dueOn ?? ''))[0];
  if (b) return `Now: borrowed${b.dueOn ? `, due ${dayText(b.dueOn, today)}` : ''}`;
  if (loans.some((l) => l.step === 'ready')) return 'Now: ready to borrow';
  if (loans.some((l) => l.step === 'on_hold')) return 'Now: on hold';
  return '';
}

/** How a count reads in a heading (R7 N4): "· 1 book", "· 194 books"; nothing when empty. */
export function headingCount(n: number, noun: [string, string] = ['book', 'books']): string {
  return n ? `${fmtNum(n)} ${n === 1 ? noun[0] : noun[1]}` : '';
}
